import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          className={cn(
            'w-full px-4 py-3 rounded-xl border bg-white dark:bg-neutral-800',
            'text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500',
            'border-neutral-200 dark:border-neutral-700',
            'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-all duration-200',
            'resize-y min-h-[120px]',
            error && 'border-accent-red focus:ring-accent-red/20 focus:border-accent-red',
            className
          )}
          {...props}
        />
        {error && (
          <p className="mt-2 text-sm text-accent-red">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{hint}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };
